import React, { useEffect, useState } from "react";
import { Modal, Form, Select, Input, Button, Space, Upload, message, Skeleton, Empty } from "antd";
import { UploadOutlined, FileTextOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import { Client } from "../../../core/api-client";
import { API_GET_ACTIVE_DOCUMENT_SUB_TYPE_LIST, API_UPLOAD_DOCUMENT } from "../../../shared/routes/private.routes";
import { DocumentType, DocumentSubType } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";

interface UploadDocumentModalProps {
  visible: boolean;
  onClose: () => void;
  staffId: string;
  documentTypes: DocumentType[];
  onSubmit: (documentTypeId: string) => void;
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  visible,
  onClose,
  staffId,
  documentTypes,
  onSubmit,
}) => {
  const [form] = useForm();
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<string | null>(null);
  const [documentSubTypes, setDocumentSubTypes] = useState<DocumentSubType[]>([]);
  const [loadingDocumentSubTypes, setLoadingDocumentSubTypes] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false); // For future API integration

  // Reset form and states when modal opens/closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedDocumentTypeId(null);
      setDocumentSubTypes([]);
      setSelectedFile(null);
    }
  }, [visible, form]);

  // Sub-types are a global list, independent of the selected document type, so
  // they load once when the dialog opens rather than on every type change.
  useEffect(() => {
    if (!visible) return;

    const fetchDocumentSubTypes = async () => {
      setLoadingDocumentSubTypes(true);
      try {
        const response = await Client.get(API_GET_ACTIVE_DOCUMENT_SUB_TYPE_LIST);
        setDocumentSubTypes(response?.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch document sub types:", error);
        showNotification("Failed to load document sub types.", "error");
        setDocumentSubTypes([]);
      } finally {
        setLoadingDocumentSubTypes(false);
      }
    };
    fetchDocumentSubTypes();
  }, [visible]);

  const handleDocumentTypeChange = (value: string) => {
    setSelectedDocumentTypeId(value);
  };

  const handleFileChange = (info: any) => {
    if (info.fileList.length > 0) {
      setSelectedFile(info.fileList[0].originFileObj);
    } else {
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedFile) {
        showNotification("Please select a file to upload.", "error");
        return;
      }

      const formData = new FormData();
      formData.append("staffId", staffId);
      formData.append("documentTypeId", values.documentTypeId);
      formData.append("documentSubTypeId", values.documentSubTypeId);
      formData.append("file", selectedFile);

      setUploading(true);
      try {
        const response = await Client.post(API_UPLOAD_DOCUMENT, formData, {
          // headers: { "Content-Type": "multipart/form-data" }, // Let Axios automatically set Content-Type for FormData
        });
        if (response.data) {
          showNotification("File uploaded successfully.", "success");
          onSubmit(values.documentTypeId);
          onClose();
        }
      } catch (error) {
        console.error("Upload failed:", error);
        showNotification("File upload failed.", "error");
      } finally {
        setUploading(false);
      }
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
      showNotification("Please fill in all required fields.", "error");
    }
  };

  const documentTypeOptions = documentTypes.map((dt) => ({
    value: dt.id,
    label: dt.documentTypeName,
  }));

  const documentSubTypeOptions = documentSubTypes.map((dst) => ({
    value: dst.id,
    label: dst.documentSubTypeName,
  }));

  return (
    <Modal
      title="Upload Document"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          loading={uploading}
        >
          Upload
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" initialValues={{ staffId }}>
        <Form.Item
          name="documentTypeId"
          label="Document Type"
          rules={[{ required: true, message: "Please select a document type!" }]}
        >
          <Select
            placeholder="Select Document Type"
            options={documentTypeOptions}
            onChange={handleDocumentTypeChange}
            loading={documentTypes.length === 0} // Show loading if document types are still being fetched
          />
        </Form.Item>

        <Form.Item
          name="documentSubTypeId"
          label="Document Sub Type"
          rules={[{ required: true, message: "Please select a document sub type!" }]}
        >
          <Select
            placeholder="Select Document Sub Type"
            options={documentSubTypeOptions}
            disabled={!selectedDocumentTypeId || loadingDocumentSubTypes}
            loading={loadingDocumentSubTypes}
          >
            {loadingDocumentSubTypes && <Select.Option value="" disabled>Loading...</Select.Option>}
            {!loadingDocumentSubTypes && documentSubTypes.length === 0 && selectedDocumentTypeId && <Select.Option value="" disabled>No sub types found</Select.Option>}
          </Select>
        </Form.Item>

        <Form.Item
          name="file"
          label="File"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
          rules={[{ required: true, message: "Please select a file!" }]}
        >
          <Upload
            beforeUpload={() => false} // Prevent automatic upload
            onChange={handleFileChange}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UploadDocumentModal;